import { useState } from "react";
import {
  BenefitSendStatus,
  IBenefitSendResponse,
  IBenefitSendResult,
} from "../../interfaces/benefit";
import { sendBenefitMessages } from "../../services/benefitService";
import { MESSAGE_COST_USD, messagingSpend } from "../../config/pricing";
import { formatUsd } from "../../utils/format";
import Modal from "../ui/Modal";
import { AlertIcon, InfoIcon, SendIcon } from "../ui/Icons";

interface ISendModalProps {
  pendingCount: number;
  onClose: () => void;
  onSent: () => void;
}

const STATUS_CLASS: Record<BenefitSendStatus, string> = {
  ENVIADO: "result-row--loaded",
  ERROR: "result-row--error",
};

const STATUS_PILL: Record<BenefitSendStatus, string> = {
  ENVIADO: "pill pill--loaded",
  ERROR: "pill pill--error",
};

export default function SendModal({
  pendingCount,
  onClose,
  onSent,
}: ISendModalProps) {
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IBenefitSendResponse | null>(null);

  const handleSend = async (): Promise<void> => {
    setIsSending(true);
    setError(null);

    try {
      const response = await sendBenefitMessages();
      setResult(response);
      onSent();
    } catch (sendError) {
      console.error("ERROR AL ENVIAR MENSAJES", sendError);
      setError(
        "No pudimos completar el envío. Verifica el estado de los registros antes de reintentar."
      );
    } finally {
      setIsSending(false);
    }
  };

  if (result) {
    const { summary } = result;

    return (
      <Modal
        title="Resultado del envío"
        subtitle="Detalle por registro procesado"
        onClose={onClose}
        wide
        footer={
          <>
            <div className="modal__foot-spacer" />
            <button type="button" className="btn btn--primary" onClick={onClose}>
              Listo
            </button>
          </>
        }
      >
        <div className="summary">
          <div className="summary__item summary__item--total">
            <div className="summary__label">Total</div>
            <div className="summary__value">{summary.totalRecords}</div>
          </div>
          <div className="summary__item summary__item--ok">
            <div className="summary__label">Enviados</div>
            <div className="summary__value">{summary.sentRecords}</div>
          </div>
          <div className="summary__item summary__item--err">
            <div className="summary__label">Con error</div>
            <div className="summary__value">{summary.failedRecords}</div>
          </div>
        </div>

        {summary.totalRecords === 0 ? (
          <div className="banner banner--info">
            <span className="banner__icon">
              <InfoIcon size={15} />
            </span>
            <span>No había registros pendientes de envío.</span>
          </div>
        ) : (
          <div className="result-list">
            {summary.records.map((record: IBenefitSendResult) => (
              <div
                className={`result-row ${STATUS_CLASS[record.status]}`}
                key={record.benefitId}
              >
                <span className="result-row__index" />
                <span className="result-row__rut">{record.DNI}</span>
                <span className="result-row__detail">
                  <span className="result-row__name">{record.name}</span>
                  {" · "}
                  <span className="result-row__msg">{record.message}</span>
                </span>
                <span className={STATUS_PILL[record.status]}>{record.status}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title="Enviar mensajes por WhatsApp"
      subtitle="Se enviará la plantilla a todos los registros sin fecha de envío"
      onClose={isSending ? undefined : onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={isSending}
          >
            Cancelar
          </button>
          <div className="modal__foot-spacer" />
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSend}
            disabled={isSending}
          >
            {isSending ? <span className="btn__spin" /> : <SendIcon size={15} />}
            {isSending ? "Enviando…" : "Confirmar envío"}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner banner--err">
          <span className="banner__icon">
            <AlertIcon size={15} />
          </span>
          <span>{error}</span>
        </div>
      )}

      <div className="banner banner--warn">
        <span className="banner__icon">
          <AlertIcon size={15} />
        </span>
        <span>
          Esta acción envía mensajes reales de WhatsApp y no se puede revertir.
          Cada persona recibe la encuesta una sola vez: los registros que ya
          tienen fecha de envío quedan fuera.
        </span>
      </div>

      <div className="cost">
        <div className="cost__row">
          <span className="cost__label">Registros pendientes de envío</span>
          <span className="cost__value">{pendingCount.toLocaleString("es-CL")}</span>
        </div>
        <div className="cost__row">
          <span className="cost__label">Costo por mensaje</span>
          <span className="cost__value">US${MESSAGE_COST_USD}</span>
        </div>
        <div className="cost__row cost__row--total">
          <span className="cost__label">Costo estimado de este envío</span>
          <span className="cost__value">{formatUsd(messagingSpend(pendingCount))}</span>
        </div>
      </div>

      <p style={{ margin: 0, fontSize: 13, color: "var(--text-soft)" }}>
        Al confirmar, se registrará la fecha de envío de cada mensaje entregado
        correctamente. Los que fallen quedarán pendientes y podrás reintentarlos
        más adelante.
      </p>
    </Modal>
  );
}
